import React, { useState, useEffect, useMemo } from "react";
import Datatable from "../../../Components/Datatable";
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../../context/AuthContext';
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import axios from "axios";

export const AdminSubjectList = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();

    // -- State'ler --
    const [courses, setCourses] = useState([]);
    const [activeData, setActiveData] = useState([]);
    const [passiveData, setPassiveData] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState("");
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    // -- Yardımcı: HTML Tag'leri Render Etme --
    const renderHtml = (rawHtml) => {
        return <span dangerouslySetInnerHTML={{ __html: rawHtml }} />;
    };

    // -- API: Kurs Listesini Getir --
    const fetchCourses = async () => {
        try {
            const res = await axios.get("https://api.heryerdeingilizce.com/api/admin/CourseManager/GetCourses", {
                headers: { Authorization: `Bearer ${user.token}` }
            });
            if (res.data.success) {
                // Sadece aktif ve silinmemiş kursları dropdown için filtrele
                const activeCourses = res.data.data.filter(c => c.isActive && !c.isDeleted);
                setCourses(activeCourses);
            }
        } catch (err) {
            setMsg("Kurs listesi yüklenemedi.");
        }
    };

    // -- API: Seçilen Kursa Göre Konuları (Subjects) Getir --
    const fetchSubjectsByCourse = async (courseId) => {
        if (!courseId) return;
        setLoading(true);
        setMsg("");

        try {
            const res = await axios.get(`https://api.heryerdeingilizce.com/api/admin/SubjectManager/GetListByCourse`, {
                params: { id: courseId },
                headers: { Authorization: `Bearer ${user.token}` }
            });

            if (res.data.success) {
                const allData = res.data.data;
                // sortId'ye göre küçükten büyüğe sırala
                const sortedData = allData.sort((a, b) => a.sortId - b.sortId);

                setActiveData(sortedData.filter(s => s.isActive && !s.isDeleted));
                setPassiveData(sortedData.filter(s => !s.isActive && !s.isDeleted));
            } else {
                setMsg(res.data.message || "Konular getirilirken bir sorun oluştu.");
            }
        } catch (err) {
            setMsg("Konular yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCourses();
    }, []);

    const handleCourseChange = (e) => {
        const id = e.target.value;
        setSelectedCourseId(id);
        fetchSubjectsByCourse(id);
    };

    // SİLME İŞLEMİ (Görsel uyarı kısmı, API bağlamak istersen 'result.isConfirmed' içine yazabilirsin)
    const handleDelete = (id) => {
        Swal.fire({
            title: t("are_you_sure") || "Emin misiniz?",
            text: "Bu konuyu silmek istediğinize emin misiniz?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t("yes_delete") || "Evet, Sil",
            cancelButtonText: t("cancel") || "İptal"
        }).then((result) => {
            if (result.isConfirmed) {
                console.log("Silme işlemi ID:", id);
                // API silme isteği buraya gelecek
                // axios.post(...)
            }
        });
    };

    // -- Tablo Sütunları --
    const columns = useMemo(() => [
        {
            Header: "Sıra (SortId)",
            accessor: "sortId",
            width: 70
        },
        {
            Header: t('subjectName') || "Konu Adı",
            accessor: 'subjectName',
            Cell: ({ value }) => renderHtml(value) // HTML tag desteği
        },
        {
            Header: t('subjectDescription') || "Konu Açıklaması",
            accessor: 'subjectDesc',
        },
        {
            Header: t('isActive') || "Durum",
            accessor: (row) => (
                <span className={`badge ${row.isActive ? 'bg-success' : 'bg-secondary'}`}>
                    {row.isActive ? "✅ Aktif" : "❌ Pasif"}
                </span>
            ),
        },
        {
            Header: t('actions') || "İşlemler",
            accessor: (row) => (
                <div className="btn-group shadow-sm">
                    <button
                        className="btn btn-warning btn-sm"
                        onClick={() => navigate(`/admin/editsubject/${row.id}`)}
                    >
                        <i className="fa fa-edit"></i> {t('edit') || "Düzenle"}
                    </button>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate(`/admin/questionlistbysubject/${row.id}`)}
                    >
                        <i className="fa fa-list"></i> {t('questions') || "Sorular"}
                    </button>
                    <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(row.id)}
                    >
                        <i className="fa fa-trash"></i> {t('delete') || "Sil"}
                    </button>
                </div>
            ),
        }
    ], [navigate, t]);

    return (
        <div className="container-fluid py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
            <div className="row justify-content-center">
                <div className="col-12 col-xl-11">

                    {/* Başlık Kartı ve Filtreleme */}
                    <div className="card shadow-sm border-0 mb-4 bg-white">
                        <div className="card-body p-4 text-center">
                            <h2 className="fw-bold text-primary mb-2">
                                <i className="fas fa-book-open me-2"></i>
                                {t("subjectList") || "Konu Listesi"}
                            </h2>
                            {msg && <div className="alert alert-danger shadow-sm">{msg}</div>}

                            <div className="row justify-content-center mt-4">
                                <div className="col-md-8 col-lg-6">
                                    <label className="form-label fw-bold text-secondary">Bağlı Olduğu Kursu Seçiniz</label>
                                    <select
                                        className="form-select form-select-lg shadow-sm"
                                        value={selectedCourseId}
                                        onChange={handleCourseChange}
                                        style={{ borderLeft: "5px solid #0d6efd" }}
                                    >
                                        <option value="">--- Kurs Seçin ---</option>
                                        {courses.map(course => (
                                            // Kurs isimlerinde de <sup> vs olabilir diye HTML replace yapabiliriz ama select içinde HTML parse çalışmaz.
                                            // Düz metin olarak görünecektir. İstersen regex ile silebiliriz ama bu haliyle de anlaşılır.
                                            <option key={course.id} value={course.id}>
                                                {course.courseName.replace(/<\/?[^>]+(>|$)/g, "")}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Yükleniyor Animasyonu */}
                    {loading ? (
                        <div className="text-center my-5 py-5">
                            <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} role="status"></div>
                            <p className="mt-3 fw-bold text-secondary">Konular Yükleniyor...</p>
                        </div>
                    ) : (
                        selectedCourseId && (
                            <div className="fade-in">
                                {/* Aktif Konular */}
                                <div className="card shadow-sm border-0 mb-5 overflow-hidden">
                                    <div className="card-header bg-success text-white py-3">
                                        <h5 className="mb-0 fw-bold">
                                            <i className="fas fa-check-circle me-2"></i> {t("subjectList") || "Aktif Konular"}
                                            <span className="badge bg-light text-success ms-2">{activeData.length}</span>
                                        </h5>
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <Datatable data={activeData} columns={columns} tableBaslik="Aktif Konular" />
                                        </div>
                                    </div>
                                </div>

                                {/* Pasif Konular */}
                                <div className="card shadow-sm border-0 bg-light mb-5 overflow-hidden">
                                    <div className="card-header py-3" style={{ backgroundColor: "#6c757d", color: "white" }}>
                                        <h5 className="mb-0 fw-bold">
                                            <i className="fas fa-archive me-2"></i> {t("passivesubjectList") || "Pasif Konular"}
                                            <span className="badge bg-light text-secondary ms-2">{passiveData.length}</span>
                                        </h5>
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <Datatable data={passiveData} columns={columns} tableBaslik="Pasif Konular" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    )}

                    {/* Boş Durum (Kurs Seçilmediğinde) */}
                    {!selectedCourseId && !loading && (
                        <div className="alert alert-info text-center shadow-sm py-4 border-0" style={{ backgroundColor: "#e9f2ff", color: "#084298" }}>
                            <i className="fa fa-arrow-up d-block mb-3 fs-2"></i>
                            Konuları listelemek için lütfen yukarıdan bir <strong>Kurs</strong> seçiniz.
                        </div>
                    )}

                </div>
            </div>

            {/* Mobil Uyumlu Animasyon ve Stiller */}
            <style jsx>{`
                .fade-in {
                    animation: fadeIn 0.5s ease-in;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(15px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @media (max-width: 768px) {
                    .card-header h5 { font-size: 1.1rem; }
                    .btn-sm { padding: 0.3rem 0.5rem; font-size: 0.8rem; }
                    .btn-group { display: flex; flex-direction: column; gap: 5px; }
                    .btn-group .btn { border-radius: 4px !important; width: 100%; }
                }
            `}</style>
        </div>
    );
};

export default AdminSubjectList;